import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { updateCompany } from '../../api/companies';
import { resolveQuartrIssuesForCompany, reportQuartrIssue } from '../../api/contentCompletion';
import {
  CompanyContentHealth,
  QuartrCheckResult,
  QuartrCompanyFields,
  QuartrMatchInfo,
} from '../../types/contentCompletion';
import { CompanyUpdate } from '../../types/company';
import { useNotification } from '../../context/NotificationContext';
import { useEnvironment } from '../../hooks/useEnvironment';
import {
  buildRejectedMatchReportRequest,
  formatFieldValue,
  formatQuartrMatchLabel,
  getProposedMatch,
} from './quartrCheckUtils';

export interface BatchQuartrReviewItem {
  companyId: string;
  companyName: string;
  company: CompanyContentHealth;
  checkResult: QuartrCheckResult;
}

interface BatchQuartrReviewDialogProps {
  open: boolean;
  onClose: () => void;
  items: BatchQuartrReviewItem[];
  onCompanySaved?: (companyId: string) => void;
  onComplete?: () => void;
  onIssueResolved?: () => void;
  readOnly?: boolean;
  title?: string;
}

const FIELD_LABELS: Record<keyof QuartrCompanyFields, string> = {
  ticker: 'Ticker',
  exchange: 'Exchange',
  quartr_company_id: 'Quartr ID',
};

const DISPLAY_FIELDS: (keyof QuartrCompanyFields)[] = [
  'ticker',
  'exchange',
  'quartr_company_id',
];

const buildUpdatePayload = (checkResult: QuartrCheckResult): CompanyUpdate => {
  const payload: CompanyUpdate = {};
  for (const field of checkResult.apply_fields) {
    const key = field as keyof CompanyUpdate;
    const value = checkResult.proposed[field as keyof QuartrCompanyFields];
    if (value !== undefined) {
      (payload as Record<string, string | null>)[key] = value ?? null;
    }
  }
  return payload;
};

const MatchDetails: React.FC<{
  label: string;
  match?: QuartrMatchInfo;
  severity?: 'info' | 'success' | 'warning';
}> = ({ label, match, severity = 'info' }) => {
  if (!match) return null;
  return (
    <Alert severity={severity} sx={{ mb: 1 }}>
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="body2">{formatQuartrMatchLabel(match)}</Typography>
    </Alert>
  );
};

const BatchQuartrReviewDialog: React.FC<BatchQuartrReviewDialogProps> = ({
  open,
  onClose,
  items,
  onCompanySaved,
  onComplete,
  onIssueResolved,
  readOnly = false,
  title,
}) => {
  const { showNotification } = useNotification();
  const { environment } = useEnvironment();
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(new Set());
  const [savedCompanyIds, setSavedCompanyIds] = useState<Set<string>>(new Set());
  const [reportedCompanyIds, setReportedCompanyIds] = useState<Set<string>>(new Set());
  const [expandedCompanyId, setExpandedCompanyId] = useState<string | false>(false);
  const [saving, setSaving] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [saveProgress, setSaveProgress] = useState<string | null>(null);
  const [reportProgress, setReportProgress] = useState<string | null>(null);
  const [confirmProductionOpen, setConfirmProductionOpen] = useState(false);
  const [productionConfirmed, setProductionConfirmed] = useState(false);
  const [pendingSaveAll, setPendingSaveAll] = useState(false);
  const [singleSaveItem, setSingleSaveItem] = useState<BatchQuartrReviewItem | null>(null);

  const saveableItems = useMemo(
    () => items.filter((item) => item.checkResult.apply_fields.length > 0),
    [items]
  );

  useEffect(() => {
    if (!open) return;
    const initial = new Set(
      saveableItems
        .filter((item) => item.checkResult.auto_select)
        .map((item) => item.companyId)
    );
    setSelectedCompanyIds(initial);
    setSavedCompanyIds(new Set());
    setReportedCompanyIds(new Set());
    setExpandedCompanyId(items[0]?.companyId ?? false);
    setSaveProgress(null);
    setReportProgress(null);
    setConfirmProductionOpen(false);
    setProductionConfirmed(false);
    setPendingSaveAll(false);
    setSingleSaveItem(null);
  }, [open, items, saveableItems]);

  const pendingItems = useMemo(
    () =>
      saveableItems.filter(
        (item) =>
          !savedCompanyIds.has(item.companyId) && !reportedCompanyIds.has(item.companyId)
      ),
    [saveableItems, savedCompanyIds, reportedCompanyIds]
  );

  const selectedPendingCount = useMemo(
    () => pendingItems.filter((item) => selectedCompanyIds.has(item.companyId)).length,
    [pendingItems, selectedCompanyIds]
  );

  const rejectedPendingCount = useMemo(
    () => pendingItems.filter((item) => !selectedCompanyIds.has(item.companyId)).length,
    [pendingItems, selectedCompanyIds]
  );

  const isBusy = saving || reporting;

  const toggleCompany = useCallback((companyId: string) => {
    setSelectedCompanyIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  }, []);

  const markSaved = (companyId: string) => {
    setSavedCompanyIds((prev) => new Set(prev).add(companyId));
    onCompanySaved?.(companyId);
    onComplete?.();
  };

  const markReported = (companyId: string) => {
    setReportedCompanyIds((prev) => new Set(prev).add(companyId));
    setSelectedCompanyIds((prev) => {
      const next = new Set(prev);
      next.delete(companyId);
      return next;
    });
    onIssueResolved?.();
  };

  const reportMismatch = async (item: BatchQuartrReviewItem) => {
    await reportQuartrIssue(
      buildRejectedMatchReportRequest(
        item.companyId,
        item.companyName,
        item.checkResult,
        item.company
      )
    );
  };

  const saveCompany = async (item: BatchQuartrReviewItem) => {
    const payload = buildUpdatePayload(item.checkResult);
    if (Object.keys(payload).length === 0) {
      throw new Error('No changes to save');
    }
    await updateCompany(item.companyId, payload);
    try {
      await resolveQuartrIssuesForCompany(item.companyId);
      onIssueResolved?.();
    } catch {
      /* save succeeded even if issue resolve fails */
    }
  };

  const handleReportCompany = async (item: BatchQuartrReviewItem) => {
    setReporting(true);
    setReportProgress(item.companyName);
    try {
      await reportMismatch(item);
      showNotification(`${item.companyName}: added to unresolved Quartr lookups`, 'success');
      markReported(item.companyId);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        `Failed to report ${item.companyName}`;
      showNotification(msg, 'error');
    } finally {
      setReporting(false);
      setReportProgress(null);
    }
  };

  const runReportMismatches = async () => {
    const toReport = pendingItems.filter((item) => !selectedCompanyIds.has(item.companyId));
    if (toReport.length === 0) {
      showNotification('No unchecked companies to report', 'warning');
      return;
    }

    setReporting(true);
    let reported = 0;
    let failed = 0;

    for (let i = 0; i < toReport.length; i += 1) {
      const item = toReport[i];
      setReportProgress(`${item.companyName} (${i + 1}/${toReport.length})`);
      try {
        await reportMismatch(item);
        markReported(item.companyId);
        reported += 1;
      } catch (err: unknown) {
        failed += 1;
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          `Failed to report ${item.companyName}`;
        showNotification(msg, 'error');
      }
    }

    setReporting(false);
    setReportProgress(null);
    if (reported > 0) {
      showNotification(
        `Reported ${reported} mismatch${reported === 1 ? '' : 'es'} to unresolved Quartr lookups`,
        'success'
      );
    }
    if (failed > 0 && reported === 0) {
      showNotification('No mismatches were reported', 'error');
    }
  };

  const handleReportMismatches = () => {
    if (rejectedPendingCount === 0) {
      showNotification('No unchecked companies to report', 'warning');
      return;
    }
    runReportMismatches();
  };

  const handleSaveCompany = async (item: BatchQuartrReviewItem) => {
    setSaving(true);
    setSaveProgress(item.companyName);
    try {
      await saveCompany(item);
      showNotification(`${item.companyName}: Quartr metadata saved`, 'success');
      markSaved(item.companyId);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        `Failed to save ${item.companyName}`;
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
      setSaveProgress(null);
    }
  };

  const runSaveAll = async () => {
    const toSave = pendingItems.filter((item) => selectedCompanyIds.has(item.companyId));
    if (toSave.length === 0) {
      showNotification('No companies selected to save', 'warning');
      return;
    }

    setSaving(true);
    let saved = 0;
    let failed = 0;

    for (let i = 0; i < toSave.length; i += 1) {
      const item = toSave[i];
      setSaveProgress(`${item.companyName} (${i + 1}/${toSave.length})`);
      try {
        await saveCompany(item);
        markSaved(item.companyId);
        saved += 1;
      } catch (err: unknown) {
        failed += 1;
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          `Failed to save ${item.companyName}`;
        showNotification(msg, 'error');
      }
    }

    setSaving(false);
    setSaveProgress(null);
    if (saved > 0) {
      showNotification(`Saved Quartr metadata for ${saved} compan${saved === 1 ? 'y' : 'ies'}`, 'success');
    }
    if (failed > 0 && saved === 0) {
      showNotification('No companies were saved', 'error');
    }
  };

  const handleSaveCompanyWithProductionCheck = (item: BatchQuartrReviewItem) => {
    if (environment === 'production') {
      setSingleSaveItem(item);
      setPendingSaveAll(false);
      setProductionConfirmed(false);
      setConfirmProductionOpen(true);
      return;
    }
    handleSaveCompany(item);
  };

  const handleSaveAll = () => {
    if (selectedPendingCount === 0) {
      showNotification('No companies selected to save', 'warning');
      return;
    }
    if (environment === 'production') {
      setPendingSaveAll(true);
      setSingleSaveItem(null);
      setProductionConfirmed(false);
      setConfirmProductionOpen(true);
      return;
    }
    runSaveAll();
  };

  const handleConfirmProduction = async () => {
    setConfirmProductionOpen(false);
    if (pendingSaveAll) {
      setPendingSaveAll(false);
      await runSaveAll();
    } else if (singleSaveItem) {
      const item = singleSaveItem;
      setSingleSaveItem(null);
      await handleSaveCompany(item);
    }
  };

  const handleClose = () => {
    if (isBusy) return;
    onClose();
  };

  const dialogTitle =
    title ||
    (readOnly
      ? `Quartr validation — ${items.length} compan${items.length === 1 ? 'y' : 'ies'}`
      : `Review Quartr check — ${items.length} compan${items.length === 1 ? 'y' : 'ies'}`);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {environment === 'production' && !readOnly && (
              <Alert severity="warning">
                You are in <strong>production</strong> mode. Saving will write to the live Neo4j
                database.
              </Alert>
            )}

            {readOnly ? (
              <Typography variant="body2" color="text.secondary">
                Read-only validation details from Quartr. No changes will be saved.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {selectedPendingCount} of {pendingItems.length} pending companies selected for save
              </Typography>
            )}

            {saving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  Saving to Neo4j{saveProgress ? ` — ${saveProgress}` : '…'}
                </Typography>
              </Box>
            )}

            {reporting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  Reporting mismatches{reportProgress ? ` — ${reportProgress}` : '…'}
                </Typography>
              </Box>
            )}

            {items.map((item) => {
              const isSaved = savedCompanyIds.has(item.companyId);
              const isReported = reportedCompanyIds.has(item.companyId);
              const isSelected = selectedCompanyIds.has(item.companyId);
              const { checkResult } = item;
              const proposedMatch = getProposedMatch(checkResult);
              const hasChanges = checkResult.apply_fields.length > 0;
              const changedFields = DISPLAY_FIELDS.filter(
                (field) => checkResult.current[field] !== checkResult.proposed[field]
              );
              const displayFields = readOnly || !hasChanges ? DISPLAY_FIELDS : changedFields;

              return (
                <Accordion
                  key={item.companyId}
                  expanded={expandedCompanyId === item.companyId}
                  onChange={(_, expanded) =>
                    setExpandedCompanyId(expanded ? item.companyId : false)
                  }
                  disabled={isBusy}
                  sx={{ opacity: isSaved || isReported ? 0.7 : 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        width: '100%',
                        pr: 1,
                      }}
                    >
                      {!readOnly && hasChanges && !isSaved && !isReported && (
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleCompany(item.companyId)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={isBusy}
                        />
                      )}
                      <Typography fontWeight={600}>{item.companyName}</Typography>
                      <Chip
                        label={checkResult.check_status.replace(/_/g, ' ')}
                        size="small"
                        variant="outlined"
                      />
                      {isSaved && <Chip label="Saved" color="success" size="small" />}
                      {isReported && <Chip label="Reported" color="warning" size="small" />}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {checkResult.summary && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {checkResult.summary}
                      </Typography>
                    )}

                    {readOnly || !hasChanges ? (
                      <MatchDetails
                        label="Quartr entity"
                        match={checkResult.quartr_match}
                        severity={readOnly ? 'success' : 'info'}
                      />
                    ) : (
                      <>
                        {checkResult.quartr_match && (
                          <MatchDetails label="Current Quartr entity" match={checkResult.quartr_match} />
                        )}
                        {proposedMatch && (
                          <MatchDetails
                            label="Proposed Quartr entity"
                            match={proposedMatch}
                            severity="warning"
                          />
                        )}
                      </>
                    )}

                    {checkResult.searched_queries.length > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Searched: {checkResult.searched_queries.join(' · ')}
                      </Typography>
                    )}

                    {checkResult.lookup_candidates.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Closest Quartr results
                        </Typography>
                        <List dense disablePadding>
                          {checkResult.lookup_candidates.map((candidate) => (
                            <ListItem key={candidate.id || candidate.name} disableGutters>
                              <ListItemText
                                primary={formatQuartrMatchLabel(candidate)}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {checkResult.warnings.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        {checkResult.warnings.map((warning) => (
                          <Typography key={warning} variant="body2">
                            {warning}
                          </Typography>
                        ))}
                      </Alert>
                    )}

                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>Current</TableCell>
                          <TableCell>{hasChanges ? 'Proposed' : 'Value'}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayFields.map((field) => {
                          const currentValue = checkResult.current[field];
                          const proposedValue = hasChanges
                            ? checkResult.proposed[field]
                            : checkResult.current[field];
                          const matchForField =
                            field === 'quartr_company_id'
                              ? hasChanges
                                ? proposedMatch || checkResult.quartr_match
                                : checkResult.quartr_match
                              : undefined;
                          return (
                            <TableRow key={field}>
                              <TableCell>{FIELD_LABELS[field]}</TableCell>
                              <TableCell>
                                {formatFieldValue(field, currentValue, checkResult.quartr_match)}
                              </TableCell>
                              <TableCell>
                                <strong>
                                  {formatFieldValue(field, proposedValue, matchForField)}
                                </strong>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>

                    {!readOnly && hasChanges && !isSaved && !isReported && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                        {!isSelected && (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            disabled={isBusy}
                            onClick={() => handleReportCompany(item)}
                          >
                            Report mismatch
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isBusy || !isSelected}
                          onClick={() => handleSaveCompanyWithProductionCheck(item)}
                        >
                          Save this company
                        </Button>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={handleClose} disabled={isBusy}>
            {readOnly || pendingItems.length === 0 ? 'Done' : 'Close'}
          </Button>
          {!readOnly && (
            <>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="outlined"
                color="warning"
                onClick={handleReportMismatches}
                disabled={isBusy || rejectedPendingCount === 0 || pendingItems.length === 0}
              >
                {reporting
                  ? 'Reporting…'
                  : `Report mismatches (${rejectedPendingCount} companies)`}
              </Button>
              <Button
                variant="contained"
                color="info"
                onClick={handleSaveAll}
                disabled={isBusy || selectedPendingCount === 0 || pendingItems.length === 0}
              >
                {saving
                  ? 'Saving…'
                  : `Save all selected (${selectedPendingCount} companies)`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmProductionOpen}
        onClose={() => {
          setConfirmProductionOpen(false);
          setPendingSaveAll(false);
          setSingleSaveItem(null);
        }}
      >
        <DialogTitle>Confirm production save</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {pendingSaveAll ? (
              <>
                You are about to update Quartr metadata for{' '}
                <strong>{selectedPendingCount} companies</strong> in the{' '}
                <strong>production</strong> Neo4j database.
              </>
            ) : (
              <>
                You are about to update Quartr metadata for{' '}
                <strong>{singleSaveItem?.companyName}</strong> in the{' '}
                <strong>production</strong> Neo4j database.
              </>
            )}
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={productionConfirmed}
                onChange={(e) => setProductionConfirmed(e.target.checked)}
              />
            }
            label="I understand this will modify production data"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmProductionOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmProduction}
            disabled={!productionConfirmed}
          >
            Save to production
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BatchQuartrReviewDialog;
